import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { IconDatabaseOff, IconRefresh } from '@tabler/icons-react';
import Editor from '@monaco-editor/react';
import { EditorModeToggle } from '../components/admin/EditorModeToggle';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import { CARD_SX } from '../styles/designSystem';
import type {
  TaskListCatalog,
  TaskListDefinition,
  TemplateTaskConfigCatalog,
} from '@techgitter/contracts';
import {
  getTemplateTaskConfigs,
  putTemplateTaskConfigs,
  clearTemplateTaskConfigsCache,
} from '../services/templateTaskConfigService';
import { getTaskLists } from '../services/taskListsService';
import { getTicketTypeTemplates } from '../services/ticketTypeTemplatesService';
import type {
  TicketTypeTemplate,
  TicketTypeTemplateCatalog,
} from '../types/ticketTypeTemplate';
import { TemplateTaskConfigGuiEditor } from '../components/admin/template-task-configs/TemplateTaskConfigGuiEditor';
import { useJsonGuiToggle, type EditorMode } from '../hooks/useJsonGuiToggle';

interface ValidationError {
  message: string;
}

export function validateCatalog(data: unknown): ValidationError[] {
  const errors: ValidationError[] = [];
  if (typeof data !== 'object' || data === null) {
    return [{ message: 'Root must be a JSON object' }];
  }
  const obj = data as Record<string, unknown>;
  if (!('schemaVersion' in obj)) {
    errors.push({ message: 'Missing required field: schemaVersion' });
  }
  const configs = obj.configs;
  if (!Array.isArray(configs)) {
    errors.push({ message: 'configs must be an array' });
    return errors;
  }
  const seenIds = new Set<string>();
  for (let i = 0; i < configs.length; i++) {
    const cfg = configs[i];
    if (typeof cfg !== 'object' || cfg === null) {
      errors.push({ message: `configs[${i}] must be an object` });
      continue;
    }
    const id = (cfg as Record<string, unknown>).ticketTypeId;
    if (!id || typeof id !== 'string') {
      errors.push({ message: `configs[${i}] missing ticketTypeId` });
    } else if (seenIds.has(id)) {
      errors.push({ message: `Duplicate ticketTypeId: ${id}` });
    } else {
      seenIds.add(id);
    }
    const sections = (cfg as Record<string, unknown>).sections;
    if (!Array.isArray(sections)) {
      errors.push({ message: `configs[${i}].sections must be an array` });
    } else {
      sections.forEach((s, si) => {
        if (typeof s !== 'object' || s === null) {
          errors.push({ message: `configs[${i}].sections[${si}] must be an object` });
          return;
        }
        const sobj = s as Record<string, unknown>;
        const isRef = 'taskListId' in sobj;
        if (isRef) {
          if (!sobj.taskListId || typeof sobj.taskListId !== 'string') {
            errors.push({
              message: `configs[${i}].sections[${si}] reference missing taskListId`,
            });
          }
        } else {
          if (!sobj.sectionId || typeof sobj.sectionId !== 'string') {
            errors.push({
              message: `configs[${i}].sections[${si}] inline section missing sectionId`,
            });
          }
          if (!Array.isArray(sobj.tasks)) {
            errors.push({
              message: `configs[${i}].sections[${si}] inline section missing tasks array`,
            });
          }
        }
      });
    }
  }
  return errors;
}

const EMPTY_CATALOG: TemplateTaskConfigCatalog = { schemaVersion: 1, configs: [] };

export const TemplateTaskConfigAdminPage: React.FC = () => {
  const [catalog, setCatalog] = useState<TemplateTaskConfigCatalog>(EMPTY_CATALOG);
  const [ticketTemplates, setTicketTemplates] = useState<TicketTypeTemplate[]>([]);
  const [taskLists, setTaskLists] = useState<TaskListDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [clearing, setClearing] = useState(false);

  const { mode, jsonText, setJsonText, switchMode, syncJsonFromValue } =
    useJsonGuiToggle<TemplateTaskConfigCatalog>({
      value: catalog,
      onChange: setCatalog,
      initialMode: 'gui',
      validate: (parsed) => validateCatalog(parsed).map((e) => e.message),
    });

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const [config, taskListsCatalog, templatesCatalog] = await Promise.all([
        getTemplateTaskConfigs(signal),
        getTaskLists(signal).catch(() => ({ schemaVersion: 1, taskLists: [] } as TaskListCatalog)),
        getTicketTypeTemplates(signal).catch(
          () => ({ templates: [] } as TicketTypeTemplateCatalog),
        ),
      ]);
      // Backend may return configs without a `sections` array; default to empty.
      const normalized: TemplateTaskConfigCatalog = {
        ...config,
        configs: (config.configs ?? []).map((cfg) => ({
          ...cfg,
          sections: cfg.sections ?? [],
        })),
      };
      setCatalog(normalized);
      setTaskLists(taskListsCatalog.taskLists ?? []);
      setTicketTemplates(templatesCatalog.templates ?? []);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load template task configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  useEffect(() => {
    if (mode === 'gui') syncJsonFromValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  // Cross-reference warnings re-derived on catalog change.
  useEffect(() => {
    const known = new Set(taskLists.map((tl) => tl.taskListId));
    const xref: string[] = [];
    (catalog.configs ?? []).forEach((cfg) => {
      (cfg.sections ?? []).forEach((section) => {
        if ('taskListId' in section) {
          const ref = section as { taskListId: string };
          if (ref.taskListId && !known.has(ref.taskListId)) {
            xref.push(`"${cfg.ticketTypeId}" references unknown taskListId "${ref.taskListId}"`);
          }
        }
      });
    });
    setWarnings(xref);
  }, [catalog, taskLists]);

  const handleSelectMode = (newMode: EditorMode) => {
    const result = switchMode(newMode);
    if (!result.ok) setError(result.error ?? 'Cannot switch modes');
  };

  const handleCatalogChange = (updated: TemplateTaskConfigCatalog) => {
    setCatalog(updated);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setValidationErrors([]);

    let dataToSave: TemplateTaskConfigCatalog;

    if (mode === 'json') {
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonText);
      } catch {
        setError('Invalid JSON');
        return;
      }
      const errors = validateCatalog(parsed);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      dataToSave = parsed as TemplateTaskConfigCatalog;
    } else {
      const errors = validateCatalog(catalog);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      dataToSave = catalog;
    }

    setSaving(true);
    try {
      const saved = await putTemplateTaskConfigs(dataToSave);
      setCatalog(saved);
      clearTemplateTaskConfigsCache().catch(() => {});
      setSuccess('Template task configs saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      const result = await clearTemplateTaskConfigsCache();
      setSuccess(result.cleared ? 'Cache cleared' : 'Cache was already empty');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const feedback = (error || success || validationErrors.length > 0 || warnings.length > 0) ? (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Validation errors:
          </Typography>
          {validationErrors.map((e, i) => (
            <Typography key={i} variant="body2">
              - {e.message}
            </Typography>
          ))}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Cross-reference warnings:
          </Typography>
          {warnings.map((w, i) => (
            <Typography key={i} variant="body2">
              - {w}
            </Typography>
          ))}
        </Alert>
      )}
    </>
  ) : null;

  return (
    <>
      {feedback && <Box sx={{ px: '16px', pt: '10px' }}>{feedback}</Box>}

      {mode === 'gui' ? (
        <TemplateTaskConfigGuiEditor
          mode={mode}
          onModeChange={handleSelectMode}
          catalog={catalog}
          ticketTemplates={ticketTemplates}
          taskLists={taskLists}
          onCatalogChange={handleCatalogChange}
          onSave={handleSave}
          onClearCache={handleClearCache}
          onReload={() => loadData()}
          saving={saving}
          clearing={clearing}
        />
      ) : (
        <Box>
          <SecondaryToolbar floating>
            <EditorModeToggle mode={mode} onChange={handleSelectMode} />
            <ToolbarSpacer />
            <ToolbarButton
              variant="primary"
              onClick={handleSave}
              title="Apply all current edits. Diffs the catalog against the live state and only writes what's changed."
            >
              {saving ? 'Saving…' : 'Save'}
            </ToolbarButton>
            <ToolbarButton
              icon={IconDatabaseOff}
              onClick={handleClearCache}
              title="Force the next read to bypass any server-side cache. Use after manual database edits or solution imports."
            >
              {clearing ? 'Clearing…' : 'Clear Cache'}
            </ToolbarButton>
            <ToolbarButton
              icon={IconRefresh}
              onClick={() => loadData()}
              title="Fetch the latest catalog from the server. Discards any unsaved edits."
            >
              Reload
            </ToolbarButton>
          </SecondaryToolbar>
          <Box sx={{ p: '12px 16px 24px' }}>
            <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
              <Editor
                height="70vh"
                defaultLanguage="json"
                value={jsonText}
                onChange={(value) => setJsonText(value ?? '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  formatOnPaste: true,
                  scrollBeyondLastLine: false,
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
